package com.dualremotetest

// Everything in this file is plain Kotlin, shared by Android and the browser, and unit-tested on a plain JVM.

const val TAG = "TanksBattleRetro"

val DIRECTION_KEYS = setOf(
    Keys.KEYCODE_DPAD_UP,
    Keys.KEYCODE_DPAD_DOWN,
    Keys.KEYCODE_DPAD_LEFT,
    Keys.KEYCODE_DPAD_RIGHT,
)
/** NUMPAD_ENTER is included because AOSP maps the CEC "Enter" UI command (0x2B) to it. */
val OK_KEYS = setOf(Keys.KEYCODE_DPAD_CENTER, Keys.KEYCODE_ENTER, Keys.KEYCODE_NUMPAD_ENTER)

/** Keys whose presence in each device's key layout is listed in the session report. */
val LAYOUT_CHECK_KEYS = intArrayOf(
    Keys.KEYCODE_DPAD_UP,
    Keys.KEYCODE_DPAD_DOWN,
    Keys.KEYCODE_DPAD_LEFT,
    Keys.KEYCODE_DPAD_RIGHT,
    Keys.KEYCODE_DPAD_CENTER,
    Keys.KEYCODE_ENTER,
    Keys.KEYCODE_BACK,
)

/**
 * Heuristic only: an UP followed by a DOWN of the same key within this gap is flagged as a "quick re-press".
 * It is consistent with a held button whose press was re-sent, but also with a very fast tap; the event log
 * cannot show whether the physical button stayed down. Field logs (yes box + Hisense TV) showed rhythmic OK
 * taps with 57–123 ms gaps, which rapid deliberate tapping can explain, so the threshold sits well below that.
 */
const val QUICK_REPRESS_MS = 30L

/** Upper bound of the wider "short re-press" bucket reported by the game diagnostics (also heuristic). */
const val SHORT_REPRESS_MS = 200L

/** AOSP HdmiCecLocalDevice injects ACTION_UP itself this long after the last <User Control Pressed>. */
const val CEC_SAFETY_TIMEOUT_MS = 550L

/**
 * True when an HDMI-CEC UP arrived ~550 ms after the previous CEC message, which matches AOSP's safety timeout.
 * This is a SUSPECTED timeout release, not a confirmed lost release: physical press durations are not recorded,
 * so a genuine ~550 ms press looks identical in the log.
 */
fun looksLikeCecTimeoutRelease(ch: Channel?, msSinceLastMessage: Long): Boolean =
    ch != null && ch.isInjected && hasSource(ch.source, Devices.SOURCE_HDMI) &&
        msSinceLastMessage in (CEC_SAFETY_TIMEOUT_MS - 15)..(CEC_SAFETY_TIMEOUT_MS + 30)

/** Immutable copy of a KeyEvent, taken in Activity.dispatchKeyEvent. */
data class KeyInput(
    val keyCode: Int,
    val keyName: String,
    val action: Int,
    val repeatCount: Int,
    val deviceId: Int,
    val source: Int,
    val flags: Int,
    val scanCode: Int,
    /** KeyEvent.getEventTime(), SystemClock.uptimeMillis() base. Kernel timestamp for evdev devices, injection time for injected events. */
    val eventTime: Long,
    val downTime: Long,
    /** SystemClock.uptimeMillis() when the Activity received it. */
    val receivedUptime: Long,
    val wallTime: Long,
) {
    val isDown get() = action == Keys.ACTION_DOWN
    val isUp get() = action == Keys.ACTION_UP
    val isCanceled get() = (flags and Keys.FLAG_CANCELED) != 0
    val dispatchLagMs get() = receivedUptime - eventTime
}

/**
 * The identity a game can actually observe for "which controller sent this".
 *
 * Real input devices are identified by deviceId. All *injected* events (HDMI-CEC via HdmiControlService,
 * vendor IR daemons, `adb shell input`, remote-control apps) are forced to deviceId -1 by InputDispatcher,
 * so for those the input source is the only remaining discriminator.
 */
data class Channel(val deviceId: Int, val source: Int) {
    val isInjected get() = deviceId < 0
    val label: String get() = when {
        deviceId == TOUCH_DEVICE_ID -> "touch"
        deviceId in ONLINE_DEVICE_IDS -> "online P${ONLINE_DEVICE_IDS.indexOf(deviceId) + 1}"
        isInjected -> "id $deviceId/${sourceShort(source)}"
        else -> "id $deviceId"
    }

    companion object {
        fun of(e: KeyInput) = Channel(e.deviceId, if (e.deviceId < 0) e.source else 0)
        fun ofDevice(deviceId: Int) = Channel(deviceId, 0)
    }
}

data class DeviceInfo(
    val id: Int,
    val name: String,
    val descriptor: String,
    val vendorId: Int,
    val productId: Int,
    val sources: Int,
    val isVirtual: Boolean,
    /** null below API 29. */
    val isExternal: Boolean?,
    val keyboardType: Int,
    val controllerNumber: Int,
    /** null below API 27. */
    val isEnabled: Boolean?,
    /** Keys from [LAYOUT_CHECK_KEYS] that the device's key layout maps (not proof the physical button exists). */
    val layoutKeys: List<String>,
    val motionAxes: Int,
) {
    val hasDpad get() = layoutKeys.containsAll(listOf("DPAD_UP", "DPAD_DOWN", "DPAD_LEFT", "DPAD_RIGHT"))

    fun kind(): String = when {
        id < 0 -> "Virtual device: receives every software-injected event"
        name.contains("cec", ignoreCase = true) -> "Kernel CEC input device (vendor-specific CEC path)"
        IR_NAME.containsMatchIn(name) -> "Probably an IR receiver (name heuristic)"
        isExternal == true -> "External device (Bluetooth or USB)"
        isVirtual -> "Virtual device"
        isExternal == false -> "Built-in device"
        else -> "Unknown (internal/external not reported below API 29)"
    }
}

private val IR_NAME = Regex("""(^|[^a-z])(ir|cir|infrared)([^a-z]|$)|ir[_-]?(keypad|remote|rx|recv)|meson-ir""", RegexOption.IGNORE_CASE)

fun channelKind(ch: Channel, dev: DeviceInfo?): String = when {
    isTouchChannel(ch) -> "On-screen touch controls (this app's own channel)"
    ch.deviceId in ONLINE_DEVICE_IDS -> "Online play (this app's own channel for that player)"
    ch.isInjected && hasSource(ch.source, Devices.SOURCE_HDMI) -> "HDMI-CEC, injected by HdmiControlService (AOSP path)"
    ch.isInjected -> "Software-injected (deviceId -1): adb, system service, vendor daemon or remote app"
    dev == null -> "Unknown (device no longer listed)"
    else -> dev.kind()
}

private val SOURCE_FLAGS = listOf(
    Devices.SOURCE_KEYBOARD to "KEYBOARD",
    Devices.SOURCE_DPAD to "DPAD",
    Devices.SOURCE_GAMEPAD to "GAMEPAD",
    Devices.SOURCE_TOUCHSCREEN to "TOUCHSCREEN",
    Devices.SOURCE_MOUSE to "MOUSE",
    Devices.SOURCE_STYLUS to "STYLUS",
    Devices.SOURCE_BLUETOOTH_STYLUS to "BT_STYLUS",
    Devices.SOURCE_TRACKBALL to "TRACKBALL",
    Devices.SOURCE_MOUSE_RELATIVE to "MOUSE_RELATIVE",
    Devices.SOURCE_TOUCHPAD to "TOUCHPAD",
    Devices.SOURCE_TOUCH_NAVIGATION to "TOUCH_NAVIGATION",
    Devices.SOURCE_ROTARY_ENCODER to "ROTARY_ENCODER",
    Devices.SOURCE_JOYSTICK to "JOYSTICK",
    Devices.SOURCE_HDMI to "HDMI",
    Devices.SOURCE_SENSOR to "SENSOR",
)

fun hasSource(sources: Int, flag: Int) = (sources and flag) == flag

fun sourceNames(sources: Int): List<String> = SOURCE_FLAGS.filter { hasSource(sources, it.first) }.map { it.second }

fun sourcesToString(sources: Int): String {
    val names = sourceNames(sources)
    val hex = "0x%08x".format(sources)
    return if (names.isEmpty()) "UNKNOWN ($hex)" else names.joinToString("|") + " ($hex)"
}

fun sourceShort(sources: Int): String = when {
    hasSource(sources, Devices.SOURCE_HDMI) -> "HDMI"
    else -> sourceNames(sources).joinToString("|").ifEmpty { "0x%x".format(sources) }
}

fun keyLabel(keyCode: Int): String = when (keyCode) {
    Keys.KEYCODE_DPAD_UP -> "UP"
    Keys.KEYCODE_DPAD_DOWN -> "DOWN"
    Keys.KEYCODE_DPAD_LEFT -> "LEFT"
    Keys.KEYCODE_DPAD_RIGHT -> "RIGHT"
    Keys.KEYCODE_DPAD_CENTER -> "CENTER"
    Keys.KEYCODE_ENTER -> "ENTER"
    Keys.KEYCODE_NUMPAD_ENTER -> "NUMPAD_ENTER"
    Keys.KEYCODE_SPACE -> "SPACE"
    Keys.KEYCODE_BUTTON_A -> "A"
    Keys.KEYCODE_BUTTON_X -> "X"
    Keys.KEYCODE_BACK -> "BACK"
    else -> "key $keyCode"
}

fun actionName(action: Int): String = when (action) {
    Keys.ACTION_DOWN -> "DOWN"
    Keys.ACTION_UP -> "UP"
    else -> "MULTIPLE"
}

fun flagsToString(flags: Int): String {
    val names = listOf(
        Keys.FLAG_FROM_SYSTEM to "FROM_SYSTEM",
        Keys.FLAG_CANCELED to "CANCELED",
        Keys.FLAG_VIRTUAL_HARD_KEY to "VIRTUAL_HARD_KEY",
        Keys.FLAG_LONG_PRESS to "LONG_PRESS",
        Keys.FLAG_CANCELED_LONG_PRESS to "CANCELED_LONG_PRESS",
        Keys.FLAG_TRACKING to "TRACKING",
        Keys.FLAG_FALLBACK to "FALLBACK",
        0x800 to "ACCESSIBILITY", // AKEY_EVENT_FLAG_IS_ACCESSIBILITY_EVENT
    ).filter { (flags and it.first) != 0 }.map { it.second }
    return "0x%x".format(flags) + if (names.isEmpty()) "" else " " + names.joinToString("|")
}

fun keyboardTypeName(type: Int): String = when (type) {
    Devices.KEYBOARD_TYPE_NONE -> "NONE"
    Devices.KEYBOARD_TYPE_NON_ALPHABETIC -> "NON_ALPHABETIC"
    Devices.KEYBOARD_TYPE_ALPHABETIC -> "ALPHABETIC"
    else -> type.toString()
}


/** Live per-channel statistics, listed in the session report. */
class ChannelStats(val channel: Channel, val colorIndex: Int) {
    var deviceName = ""
    var kind = ""
    var events = 0
    var downs = 0
    var ups = 0
    var repeats = 0
    var lastKey = ""
    var lastWall = 0L
    var maxLagMs = 0L
    var quickRepresses = 0
    var duplicateDowns = 0
    var orphanUps = 0
    var cancels = 0
    /** HDMI-CEC UPs that arrived ~550 ms after the last CEC message (suspected Android timeout releases). */
    var suspectedTimeoutReleases = 0
    var avgFirstRepeatMs = 0L
    var avgRepeatMs = 0L
    var lastHoldMs = 0L
    var maxHoldMs = 0L
    val keysSeen = ArrayList<String>()
    /** keyCode -> eventTime of its DOWN; what this channel is holding right now. */
    val held = LinkedHashMap<Int, Long>()

    private val lastUpAt = HashMap<Int, Long>()
    private val lastEventAt = HashMap<Int, Long>()
    private var firstRepeatSum = 0L
    private var firstRepeatN = 0
    private var repeatSum = 0L
    private var repeatN = 0

    /** Returns a human-readable anomaly, if this event revealed one. */
    fun record(e: KeyInput, name: String, kindText: String): String? {
        deviceName = name
        kind = kindText
        events++
        lastKey = "${e.keyName} ${actionName(e.action)}"
        lastWall = e.wallTime
        if (e.dispatchLagMs > maxLagMs) maxLagMs = e.dispatchLagMs
        if (e.keyName !in keysSeen) keysSeen.add(e.keyName)
        val t = e.eventTime
        var anomaly: String? = null
        when {
            e.isDown && e.repeatCount == 0 -> {
                downs++
                if (held.containsKey(e.keyCode)) {
                    duplicateDowns++
                    anomaly = "${e.keyName}: second DOWN without UP"
                }
                lastUpAt[e.keyCode]?.let { up ->
                    if (t - up <= QUICK_REPRESS_MS) {
                        quickRepresses++
                        anomaly = "${e.keyName}: quick re-press, UP→DOWN after ${t - up} ms (previous press $lastHoldMs ms) – " +
                            "heuristic: a re-sent held button or a very fast tap; the log cannot tell which"
                    }
                }
                held[e.keyCode] = t
                lastEventAt[e.keyCode] = t
            }
            e.isDown -> {
                repeats++
                val prev = lastEventAt[e.keyCode]
                if (prev != null) {
                    if (e.repeatCount == 1) {
                        firstRepeatSum += t - prev; firstRepeatN++
                        avgFirstRepeatMs = firstRepeatSum / firstRepeatN
                    } else {
                        repeatSum += t - prev; repeatN++
                        avgRepeatMs = repeatSum / repeatN
                    }
                }
                if (!held.containsKey(e.keyCode)) held[e.keyCode] = e.downTime
                lastEventAt[e.keyCode] = t
            }
            e.isUp -> {
                ups++
                if (e.isCanceled) {
                    cancels++
                    anomaly = "${e.keyName}: UP with FLAG_CANCELED (window focus changed while held)"
                }
                val start = held.remove(e.keyCode)
                if (start == null) {
                    orphanUps++
                    if (anomaly == null) anomaly = "${e.keyName}: UP without a matching DOWN"
                } else {
                    lastHoldMs = t - start
                    if (lastHoldMs > maxHoldMs) maxHoldMs = lastHoldMs
                    if (looksLikeCecTimeoutRelease(channel, t - (lastEventAt[e.keyCode] ?: start))) {
                        suspectedTimeoutReleases++
                        if (anomaly == null) anomaly = "${e.keyName}: UP ~550 ms after the last CEC message – suspected " +
                            "Android timeout release (press counted as $lastHoldMs ms; physical press length not recorded)"
                    }
                }
                lastUpAt[e.keyCode] = t
                lastEventAt.remove(e.keyCode)
            }
        }
        return anomaly
    }

    fun summary(): String =
        "events $events (down $downs, up $ups, repeat $repeats) · first-repeat ~${avgFirstRepeatMs} ms · " +
            "repeat interval ~${avgRepeatMs} ms · hold last/max $lastHoldMs/$maxHoldMs ms · quick re-presses ≤${QUICK_REPRESS_MS}ms (heuristic) $quickRepresses · " +
            "dup-down $duplicateDowns · orphan-up $orphanUps · canceled $cancels · suspected CEC timeout UPs $suspectedTimeoutReleases · " +
            "max dispatch lag $maxLagMs ms"
}

data class KeyLogEntry(
    val seq: Long,
    val input: KeyInput,
    val deviceName: String,
    val channel: Channel,
    val consumed: Boolean,
    val disposition: String,
    val screen: String,
) {
    fun toLogLine(): String = with(input) {
        "KEY ${actionName(action)} $keyName rpt=$repeatCount dev=$deviceId \"$deviceName\" " +
            "src=${sourceShort(source)} scan=$scanCode flags=${flagsToString(flags)} lag=${dispatchLagMs}ms " +
            "consumed=$consumed ($disposition) screen=$screen"
    }

    fun toReportLine(): String = with(input) {
        "%s  %-13s %-4s rpt=%-3d dev=%-4d %-24s src=%-14s scan=%-4d lag=%-4s consumed=%-5s %s".format(
            clockTime(wallTime), keyName, actionName(action), repeatCount, deviceId,
            "\"" + deviceName.take(22) + "\"", sourceShort(source), scanCode, "${dispatchLagMs}ms",
            consumed, disposition,
        )
    }
}

class DiagnosticsStore(private val maxKeyLog: Int = 5000) {
    /** Set by the Activity to android.util.Log; a no-op in unit tests. */
    var logcat: (String) -> Unit = {}

    val devices = ArrayList<DeviceInfo>()
    val keyLog = ArrayList<KeyLogEntry>()
    val diagLines = ArrayList<String>()
    val channels = LinkedHashMap<Channel, ChannelStats>()
    var environment: List<Pair<String, String>> = emptyList()
    var monitorCapture = false
    var showAllKeys = false
    var lastEntry: KeyLogEntry? = null
    var totalKeyEvents = 0L
        private set

    private var seq = 0L

    fun log(message: String) {
        diagLines.add("${clockTime(currentTimeMillis())}  $message")
        if (diagLines.size > MAX_DIAG_LINES + 100) diagLines.subList(0, 100).clear()
        logcat(message)
    }

    fun deviceById(id: Int): DeviceInfo? = devices.firstOrNull { it.id == id }

    fun setDevices(list: List<DeviceInfo>) {
        devices.clear()
        devices.addAll(list)
    }

    fun recordKey(e: KeyInput, deviceName: String, consumed: Boolean, disposition: String, screen: String) {
        val ch = Channel.of(e)
        val stats = channels.getOrPut(ch) {
            ChannelStats(ch, channels.size).also {
                log("NEW INPUT CHANNEL ${ch.label} \"$deviceName\" – ${channelKind(ch, deviceById(e.deviceId))}")
            }
        }
        stats.record(e, deviceName, channelKind(ch, deviceById(e.deviceId)))?.let { log("ANOMALY ${ch.label}: $it") }
        val entry = KeyLogEntry(++seq, e, deviceName, ch, consumed, disposition, screen)
        keyLog.add(entry)
        if (keyLog.size > maxKeyLog + 500) keyLog.subList(0, 500).clear()
        lastEntry = entry
        totalKeyEvents++
        logcat(entry.toLogLine())
    }

    fun clearKeyLog() {
        keyLog.clear()
        channels.clear()
        lastEntry = null
        log("Key log and channel statistics cleared")
    }

    fun channelsForDevice(deviceId: Int): List<ChannelStats> =
        channels.values.filter { it.channel.deviceId == deviceId }.sortedBy { it.colorIndex }

    /** [sections] are (heading, body) pairs inserted after the environment block. */
    fun buildReport(title: String, sections: List<Pair<String, String>>): String = buildString {
        appendLine(title)
        appendLine("Generated ${reportTimestamp()}")
        appendLine()
        appendLine("== Environment ==")
        environment.forEach { (k, v) -> appendLine("$k: $v") }
        appendLine()
        appendLine("== How to read the timings ==")
        TIMING_LEGEND.forEach { appendLine("  - $it") }
        appendLine()
        sections.forEach { (heading, body) ->
            appendLine("== $heading ==")
            append(body)
            if (!body.endsWith("\n")) appendLine()
            appendLine()
        }
        appendLine("== Input devices (${devices.size}) ==")
        devices.forEach { d ->
            appendLine("#${d.id} \"${d.name}\"")
            appendLine("   descriptor=${d.descriptor}")
            appendLine("   vendor=0x%04x product=0x%04x virtual=%s external=%s enabled=%s".format(
                d.vendorId, d.productId, d.isVirtual, d.isExternal ?: "n/a(<API29)", d.isEnabled ?: "n/a(<API27)"))
            appendLine("   sources=${sourcesToString(d.sources)} keyboardType=${keyboardTypeName(d.keyboardType)} controllerNumber=${d.controllerNumber} motionAxes=${d.motionAxes}")
            appendLine("   layoutKeys=${d.layoutKeys.joinToString(",")}")
            appendLine("   guess=${d.kind()}")
        }
        appendLine()
        appendLine("== Input channels that produced key events (${channels.size}) ==")
        channels.values.sortedBy { it.colorIndex }.forEach { s ->
            appendLine("${s.channel.label} \"${s.deviceName}\" – ${s.kind}")
            appendLine("   keys: ${s.keysSeen.joinToString(",")}")
            appendLine("   ${s.summary()}")
        }
        appendLine()
        appendLine("== Diagnostic log ==")
        diagLines.forEach { appendLine(it) }
        appendLine()
        appendLine("== Key events (${keyLog.size} kept of $totalKeyEvents received, oldest first) ==")
        keyLog.forEach { appendLine(it.toReportLine()) }
    }

    companion object {
        const val MAX_DIAG_LINES = 400
        val TIMING_LEGEND = listOf(
            "lag = time from a key event's own timestamp to this app receiving it (dispatch lag). It is NOT " +
                "button-to-screen latency, which this app cannot measure.",
            "repeat intervals = spacing between repeat events from a remote or the system. They do not establish " +
                "smooth movement or end-to-end latency.",
            "\"quick re-press\" counts are heuristics: the log cannot show whether the physical button stayed down.",
            "\"suspected CEC timeout\" = an UP ~550 ms after the last CEC message (matches Android's safety timeout). " +
                "Unconfirmed: physical press durations are not recorded.",
        )
    }
}

/** Process-wide state, so a recreated Activity keeps the collected data. */
object AppState {
    /** A large log, so the report covers a whole match. */
    val gameDiagnostics = DiagnosticsStore(maxKeyLog = 20_000)
    val tankMatch = TankMatch(CoopInput(gameDiagnostics::log), gameDiagnostics::log)
    /** Online play outlives the Activity (an online game survives a recreated screen). */
    var online: OnlinePlay? = null
}
